<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import SwitchTheme from "@/components/elements/themeCheckbox2/switchTheme.vue";
import { UserStore } from "@/store/userStore";
import { RouterLink } from "vue-router";

const userStore = UserStore();
const themeDropdownOpen = ref(false);
const mobileMenuOpen = ref(false);
const themeDropdownRef = ref<HTMLElement | null>(null);
const mobileMenuRef = ref<HTMLElement | null>(null);
const landingPageRef = ref<HTMLElement | null>(null);
let revealObserver: IntersectionObserver | null = null;

const toggleThemeDropdown = () => {
  themeDropdownOpen.value = !themeDropdownOpen.value;
};

const closeThemeDropdown = () => {
  themeDropdownOpen.value = false;
};

const toggleMobileMenu = () => {
  mobileMenuOpen.value = !mobileMenuOpen.value;
  closeThemeDropdown();
};

const closeMobileMenu = () => {
  mobileMenuOpen.value = false;
};

const handleDocumentClick = (event: MouseEvent) => {
  if (!(event.target instanceof Node)) return;

  if (
    themeDropdownRef.value &&
    !themeDropdownRef.value.contains(event.target)
  ) {
    closeThemeDropdown();
  }

  if (mobileMenuRef.value && !mobileMenuRef.value.contains(event.target)) {
    closeMobileMenu();
  }
};

const handleResize = () => {
  if (window.innerWidth > 650) {
    closeMobileMenu();
  }
};

const isScrolled = ref(false);

const handleScroll = () => {
  isScrolled.value = window.scrollY > 0;
};

const initializeScrollReveal = async () => {
  await nextTick();

  revealObserver?.disconnect();

  if (!landingPageRef.value) return;

  const revealElements =
    landingPageRef.value.querySelectorAll<HTMLElement>(".scroll-reveal");

  revealObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        entry.target.classList.add("is-visible");
        revealObserver?.unobserve(entry.target);
      });
    },
    {
      threshold: 0.2,
      rootMargin: "0px 0px -10% 0px",
    }
  );

  revealElements.forEach((element) => {
    revealObserver?.observe(element);
  });
};

watch(
  () => userStore.theme,
  (newVal) => {
    // eslint-disable-next-line no-storage/no-browser-storage
    localStorage.setItem("theme", newVal ? "light" : "dark");
    userStore.setTheme();
    window.addEventListener("scroll", handleScroll);
  }
);

onMounted(() => {
  // eslint-disable-next-line no-storage/no-browser-storage
  userStore.theme = localStorage.getItem("theme") === "light";
  userStore.setTheme();
  document.addEventListener("click", handleDocumentClick);

  handleScroll();
  void initializeScrollReveal();
  window.addEventListener("scroll", handleScroll);
  window.addEventListener("resize", handleResize);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleDocumentClick);
  window.removeEventListener("resize", handleResize);
  window.removeEventListener("scroll", handleScroll);
  revealObserver?.disconnect();
});
</script>
<template>
  <div class="nav-wrapper" :class="{ scrolled: isScrolled }">
    <div class="nav-container">
      <RouterLink id="nav-logo" to="/landing">
        <img
          :src="
            userStore.theme
              ? '/src/assets/images/LandingLogoLight.svg'
              : '/src/assets/images/LandingLogo.svg'
          "
          alt="OmniTasker"
        />
      </RouterLink>

      <div class="header-actions">
        <div ref="themeDropdownRef" class="theme-dropdown-wrapper" @click.stop>
          <button
            type="button"
            class="theme-dropdown-toggle"
            :aria-expanded="themeDropdownOpen"
            aria-label="Toggle theme menu"
            @click.stop="toggleThemeDropdown"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6 9L12 15L18 9"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
          <transition name="fade-down">
            <div
              v-if="themeDropdownOpen"
              class="theme-dropdown-menu"
              @click.stop
            >
              <div class="theme-dropdown-row">
                <span>Color mode</span>
                <SwitchTheme v-model="userStore.theme" />
              </div>
            </div>
          </transition>
        </div>
        <button
          type="button"
          class="mobile-nav-toggle"
          :aria-expanded="mobileMenuOpen"
          aria-label="Toggle navigation menu"
          @click.stop="toggleMobileMenu"
        >
          <svg
            v-if="!mobileMenuOpen"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M4 7H20"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            />
            <path
              d="M4 12H20"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            />
            <path
              d="M4 17H20"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            />
          </svg>
          <svg
            v-else
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M6 6L18 18"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            />
            <path
              d="M18 6L6 18"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            />
          </svg>
        </button>
        <div class="btns-wrapper">
          <RouterLink to="/auth/login" class="primary-outlined-button">
            Sign in
          </RouterLink>
          <RouterLink
to="/contact"
class="primary-filled-button"
            >Contact us</RouterLink
          >
        </div>
        <transition name="fade-down">
          <div
            v-if="mobileMenuOpen"
            ref="mobileMenuRef"
            class="mobile-nav-menu"
            @click.stop
          >
            <RouterLink
              :to="{ name: 'Login' }"
              class="primary-outlined-button"
              @click="closeMobileMenu"
            >
              Sign in
            </RouterLink>
            <RouterLink
              :to="{ name: 'Contact' }"
              class="primary-filled-button"
              @click="closeMobileMenu"
            >
              Contact us
            </RouterLink>
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
a {
  display: inline-flex;
  padding: 0.7rem 1.25rem;
  justify-content: center;
  align-items: center;
  gap: 0.625rem;
  border-radius: 30rem;
  font-size: var(--figma-font-size-xl, 1.5rem);
  font-style: normal;
  font-weight: var(--figma-font-weight-bold, 700);
  text-decoration: none;
}

button {
  display: inline-flex;
  padding: 0.7rem 1.25rem;
  justify-content: center;
  align-items: center;
  gap: 0.625rem;
  border-radius: 30rem;
  font-size: var(--figma-font-size-xl, 1.5rem);
  font-style: normal;
  font-weight: var(--figma-font-weight-bold, 700);
}

.primary-filled-button {
  border: 2px solid var(--figma-color-button-primary-filled-stroke, #163f97);
  background: var(--figma-color-button-primary-filled-background, #1a52c2);

  /* button/primary-raised */
  box-shadow: 0 -2px 4px 0 var(
        --figma-color-button-primary-filled-shadow,
        #163f97
      ) inset,
    0 4px 4px 0 var(--figma-color-button-primary-filled-highlight, #1f63e0)
      inset,
    0 4px 8px -2px var(--figma-color-shadow-md-1, rgba(10, 13, 18, 0.1)),
    0 2px 4px -2px var(--figma-color-shadow-md-2, rgba(10, 13, 18, 0.06));

  color: var(--figma-color-button-primary-filled-text, #fcfdfe);
}

.primary-outlined-button {
  border: 2px solid var(--figma-color-button-primary-outlined-stroke, #163f97);
  background: var(--figma-color-button-primary-outlined-fill, #1a52c2);
  color: var(--figma-color-button-primary-outlined-text, #0a1d47);
}

#nav-logo {
  padding: 0;
}

.nav-container {
  max-width: 1262px;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.nav-wrapper {
  position: sticky;
  top: 0;
  z-index: 20;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: transparent;
  padding-block: var(--figma-space-xl);
  transition: background 300ms ease;

  &.scrolled {
    background: color-mix(
      in srgb,
      var(--figma-color-background-bg-dark) 90%,
      transparent
    );
    backdrop-filter: blur(10px);
  }
}

.header-actions {
  position: relative;
  z-index: 21;
  display: flex;
  align-items: center;
  gap: var(--figma-space-xs);
}

.theme-dropdown-wrapper {
  position: relative;
  z-index: 22;
}

.theme-dropdown-toggle {
  position: relative;
  z-index: 23;
  width: 3.5rem;
  height: 3.5rem;
  padding: 0;
  cursor: pointer;
  border: 1px solid var(--figma-color-border-card, #3f404b);
  border-radius: 999px;
  background: var(--figma-color-background-bg, #121521);
  color: var(--figma-color-text-text);
  box-shadow: none;
  transition: transform 180ms ease;

  svg {
    width: 1.5rem;
    height: 1.5rem;
  }
}

.theme-dropdown-toggle[aria-expanded="true"] {
  transform: rotate(180deg);
}

.theme-dropdown-menu {
  position: absolute;
  pointer-events: auto;
  top: calc(100% + 0.75rem);
  right: 0;
  z-index: 10;
  min-width: 12rem;
  padding: var(--figma-space-md);
  border: 1px solid var(--figma-color-border-card, #3f404b);
  border-radius: var(--border-radius-xl, 1.5rem);
  background: var(--figma-color-background-bg, #121521);
  box-shadow: 0 14px 32px rgba(10, 13, 18, 0.24);
}

.theme-dropdown-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--figma-space-md);

  span {
    font-size: var(--figma-font-size-base);
    font-weight: var(--figma-font-weight-semi-bold);
    color: var(--figma-color-text-text);
    white-space: nowrap;
  }
}

.btns-wrapper {
  display: flex;
  gap: var(--figma-space-xs);
}

.mobile-nav-toggle {
  display: none;
  width: 3.5rem;
  height: 3.5rem;
  padding: 0;
  border: 1px solid var(--figma-color-border-card, #3f404b);
  border-radius: 999px;
  background: var(
    --figma-color-background-bg,
    var(--figma-color-background-bg-dark)
  );
  color: var(--figma-color-text-text);
  box-shadow: none;

  svg {
    width: 1.5rem;
    height: 1.5rem;
  }
}

.mobile-nav-menu {
  position: absolute;
  top: calc(100% + 0.75rem);
  right: 0;
  z-index: 15;
  display: none;
  min-width: 13rem;
  padding: 1rem;
  border: 1px solid var(--figma-color-border-card, #3f404b);
  border-radius: var(--border-radius-xl, 1.5rem);
  background: var(
    --figma-color-background-bg,
    var(--figma-color-background-bg-dark)
  );
  box-shadow: 0 14px 32px rgba(10, 13, 18, 0.24);
  flex-direction: column;
  gap: 0.75rem;

  a,
  button {
    width: 100%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .scroll-reveal {
    opacity: 1;
    transform: none;
    transition: none;
  }
}

@media (max-width: 1400px) {
  .nav-container {
    padding-inline: var(--figma-space-3xl);
  }

  .nav-container {
    padding-inline: var(--figma-space-3xl);
  }
}

@media (max-width: 700px) {
  .nav-container {
    padding-inline: var(--figma-space-2xl);
  }

  button,
  a {
    font-size: var(--figma-typography-font-size-text-lg);
    padding: 0.5rem 1rem;
  }
}

@media (max-width: 650px) {
  .header-actions {
    gap: 0.5rem;
  }

  .btns-wrapper {
    display: none;
  }

  .mobile-nav-toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .mobile-nav-menu {
    display: flex;
  }
}
</style>
